import axios from 'axios'
import toast from 'react-hot-toast'

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5001/api'

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 120000,
})

// Request interceptor to add language header and auth token
api.interceptors.request.use(
  (config) => {
    const language = localStorage.getItem('admin-language') || 'en'
    config.headers.lang = language
    
    // Add auth token if available
    const token = localStorage.getItem('admin_token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response) {
      const { status, data } = error.response

      switch (status) {
        case 400:
          toast.error(data.message || 'Bad request')
          break
        case 401:
          // If unauthorized, clear token
          localStorage.removeItem('admin_token')
          // Only redirect and show toast if not already on login page
          const currentPath = window.location.pathname
          if (currentPath !== '/login') {
            toast.error(data.message || 'Session expired. Please login again.')
            window.location.href = '/login'
          } else {
            // Already on login page, just show error
            toast.error(data.message || 'Invalid credentials')
          }
          break
        case 403:
          toast.error(data.message || 'Access forbidden')
          break
        case 404:
          toast.error(data.message || 'Resource not found')
          break
        case 422:
          if (data.errors) {
            data.errors.forEach(error => {
              toast.error(`${error.field}: ${error.message}`)
            })
          } else {
            toast.error(data.message || 'Validation error')
          }
          break
        case 500:
          toast.error(data.message || 'Internal server error')
          break
        default:
          toast.error(data.message || 'An error occurred')
      }
    } else if (error.request) {
      toast.error('Network error - please check your connection')
    } else {
      toast.error('An unexpected error occurred')
    }

    return Promise.reject(error)
  }
)

// API endpoints
export const vehiclesApi = {
  getAll: (params = {}) => api.get('/vehicles', { params }),
  getStats: () => api.get('/vehicles/vehicles-stats'),
  getById: (id) => api.get(`/vehicles/${id}`),
  create: (data) => api.post('/vehicles', data),
  update: (id, data) => api.put(`/vehicles/${id}`, data),
  delete: (id) => api.delete(`/vehicles/${id}`),
  updateImageType: (vehicleId, imageId, type) =>
    api.patch(`/vehicles/${vehicleId}/images/${imageId}/type`, { type }),
}

export const imagesApi = {
  upload: (formData) => api.post('/images/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  upload360: (formData) => api.post('/images/upload-360', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  getAll: (params = {}) => api.get('/images', { params }),
  getById: (id) => api.get(`/images/${id}`),
  update: (id, data) => api.put(`/images/${id}`, data),
  delete: (id) => api.delete(`/images/${id}`),
  linkToVehicle: (imageId, vehicleId, type = 'main', sortOrder = 0) =>
    api.post(`/images/${imageId}/link-vehicle`, { vehicle_id: vehicleId, type, sort_order: sortOrder }),
  unlinkFromVehicle: (imageId, vehicleId) =>
    api.delete(`/images/${imageId}/unlink-vehicle/${vehicleId}`),
}

export const pdfsApi = {
  upload: (formData) => api.post('/pdfs/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  uploadSimple: (formData) => api.post('/pdfs/upload-simple', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  getAll: (params = {}) => api.get('/pdfs', { params }),
  getById: (id) => api.get(`/pdfs/${id}`),
  delete: (id) => api.delete(`/pdfs/${id}`),
}

export const enumsApi = {
  getAll: () => api.get('/enums'),
  fuelTypes: {
    getAll: (params = {}) => api.get('/enums/fuel-types', { params }),
    create: (data) => api.post('/enums/fuel-types', data),
    update: (id, data) => api.put(`/enums/fuel-types/${id}`, data),
    delete: (id) => api.delete(`/enums/fuel-types/${id}`),
  },
  colors: {
    getAll: (params = {}) => api.get('/enums/colors', { params }),
    create: (data) => api.post('/enums/colors', data),
    update: (id, data) => api.put(`/enums/colors/${id}`, data),
    delete: (id) => api.delete(`/enums/colors/${id}`),
  },
  transmissions: {
    getAll: (params = {}) => api.get('/enums/transmissions', { params }),
    create: (data) => api.post('/enums/transmissions', data),
    update: (id, data) => api.put(`/enums/transmissions/${id}`, data),
    delete: (id) => api.delete(`/enums/transmissions/${id}`),
  },
  bodyTypes: {
    getAll: (params = {}) => api.get('/enums/body-types', { params }),
    create: (data) => api.post('/enums/body-types', data),
    update: (id, data) => api.put(`/enums/body-types/${id}`, data),
    delete: (id) => api.delete(`/enums/body-types/${id}`),
  },
  deliveryStatuses: {
    getAll: (params = {}) => api.get('/enums/delivery-statuses', { params }),
    create: (data) => api.post('/enums/delivery-statuses', data),
    update: (id, data) => api.put(`/enums/delivery-statuses/${id}`, data),
    delete: (id) => api.delete(`/enums/delivery-statuses/${id}`),
  },
  saleStatuses: {
    getAll: (params = {}) => api.get('/enums/sale-statuses', { params }),
    create: (data) => api.post('/enums/sale-statuses', data),
    update: (id, data) => api.put(`/enums/sale-statuses/${id}`, data),
    delete: (id) => api.delete(`/enums/sale-statuses/${id}`),
    toggleStatus: (id) => api.patch(`/enums/sale-statuses/${id}/toggle-status`),
  },
  options: {
    getAll: (params = {}) => api.get('/enums/options', { params }),
    create: (data) => api.post('/enums/options', data),
    update: (id, data) => api.put(`/enums/options/${id}`, data),
    delete: (id) => api.delete(`/enums/options/${id}`),
    toggleStatus: (id) => api.patch(`/enums/options/${id}/toggle-status`),
  },
  warrantyTypes: {
    getAll: (params = {}) => api.get('/enums/warranty-types', { params }),
    create: (data) => api.post('/enums/warranty-types', data),
    update: (id, data) => api.put(`/enums/warranty-types/${id}`, data),
    delete: (id) => api.delete(`/enums/warranty-types/${id}`),
  },
  trims: {
    getAll: (params = {}) => api.get('/enums/trims', { params }),
    create: (data) => api.post('/enums/trims', data),
    update: (id, data) => api.put(`/enums/trims/${id}`, data),
    delete: (id) => api.delete(`/enums/trims/${id}`),
  },
  vehicleTypes: {
    getAll: (params = {}) => api.get('/enums/vehicle-types', { params }),
    create: (data) => api.post('/enums/vehicle-types', data),
    update: (id, data) => api.put(`/enums/vehicle-types/${id}`, data),
    delete: (id) => api.delete(`/enums/vehicle-types/${id}`),
  },
  seoTexts: {
    getAll: (params = {}) => api.get('/enums/seo-texts', { params }),
    getById: (id) => api.get(`/enums/seo-texts/${id}`),
    create: (data) => api.post('/enums/seo-texts', data),
    update: (id, data) => api.put(`/enums/seo-texts/${id}`, data),
    delete: (id) => api.delete(`/enums/seo-texts/${id}`),
  },
}

export const brandsApi = {
  getAll: (params = {}) => api.get('/catalog/manufacturers', { params }),
  getById: (id) => api.get(`/catalog/manufacturers/${id}`),
  create: (data) => api.post('/catalog/manufacturers', data),
  update: (id, data) => api.put(`/catalog/manufacturers/${id}`, data),
  delete: (id) => api.delete(`/catalog/manufacturers/${id}`),
  toggleStatus: (id) => api.patch(`/catalog/manufacturers/${id}/toggle-status`),
}

export const modelsApi = {
  getAll: (params = {}) => api.get('/catalog/model-groups', { params }),
  getByManufacturer: (manufacturerId) => api.get(`/catalog/model-groups/by-manufacturer/${manufacturerId}`),
  getById: (id) => api.get(`/catalog/model-groups/${id}`),
  create: (data) => api.post('/catalog/model-groups', data),
  update: (id, data) => api.put(`/catalog/model-groups/${id}`, data),
  delete: (id) => api.delete(`/catalog/model-groups/${id}`),
  toggleStatus: (id) => api.patch(`/catalog/model-groups/${id}/toggle-status`),
}

export const analyticsApi = {
  getOverview: () => api.get('/analytics/overview'),
  getMostViewed: (params = {}) => api.get('/analytics/most-viewed', { params }),
  getTopFilters: (params = {}) => api.get('/analytics/top-filters', { params }),
  getFilterSummary: (params = {}) => api.get('/analytics/filter-summary', { params }),
  getViewsTrend: (params = {}) => api.get('/analytics/views-trend', { params }),
  getTopBrands: (params = {}) => api.get('/analytics/top-brands', { params }),
}

export const authApi = {
  login: (username, password) => api.post('/auth/login', { username, password }),
  verifyToken: () => api.get('/auth/verify-token'),
  changePassword: (currentPassword, newPassword, confirmPassword) =>
    api.post('/auth/change-password', {
      current_password: currentPassword,
      new_password: newPassword,
      confirm_password: confirmPassword
    }),
  createAdmin: (data) => api.post('/auth/create-admin', data), // Backend only
}

export default api
